import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createThunkResponse,
  deleteThunkResponse,
  getThunkResponse,
  players,
  roundCourses,
  roundTeeChoices,
  roundTeePlayerChoices,
  roundTees,
  saveThunkResponse,
  teamRounds,
  tournament,
  tournamentTeams, tournamentV3,
  uploadThunkResponse,
  validateThunkResponse,
  waitListMembers,
} from "../app/api";
import {gsApi} from "../auth/auth";
import {
  getIDRelationship,
  getOpts,
  getResponse,
  getSerializedData,
  shortDelay,
} from "../helpers/JSONapi";

//get
export const getTournamentTeams = createAsyncThunk(
  "teams/getTournamentTeams",
  async (id, thunkAPI) => {
    const response = await gsApi.get(`${tournamentV3}/${id}/tournament-teams`);
    return response;
  }
);

export const getTournamentTeam = createAsyncThunk(
  "teams/getTournamentTeam",
  async (id, thunkAPI) => {
    const response = await gsApi.get(`${tournamentTeams}/${id}`);
    return response;
  }
);

export const getTournamentTeamTeeChoices = createAsyncThunk(
  "teams/getTournamentTeamTeeChoices",
  async (id, thunkAPI) => {
    const response = await gsApi.get(
      `${tournamentTeams}/${id}/round-tee-choices`
    );
    let tees = [];
    for (const teeChoice of response) {
      const t = await gsApi.get(`${roundTees}/${teeChoice.roundTee.id}`);
      const c = await gsApi.get(
        `${roundCourses}/${teeChoice?.roundCourse?.id}`
      );
      tees.push({ ...teeChoice, roundTee: t, roundCourse: c });
    }
    return tees;
  }
);

export const getTournamentTeamPlayerTeeChoices = createAsyncThunk(
  "teams/getTournamentTeamPlayerTeeChoices",
  async (id, thunkAPI) => {
    const response = await gsApi.get(
      `${players}/${id}/round-tee-player-choices`
    );
    let tees = [];
    for (const teeChoice of response) {
      const t = await gsApi.get(`${roundTees}/${teeChoice?.roundTee?.id}`);
      const c = await gsApi.get(
        `${roundCourses}/${teeChoice?.roundCourse?.id}`
      );
      tees.push({ ...teeChoice, roundTee: t, roundCourse: c });
    }
    return tees;
  }
);

export const getTournamentTeamPlayer = createAsyncThunk(
  "teams/getTournamentTeamPlayer",
  async (id, thunkAPI) => {
    const response = await gsApi.get(`${players}/${id}`);
    return response;
  }
);

export const getUnassignedPlayer = createAsyncThunk(
  "teams/getUnassignedPlayer",
  async (id, thunkAPI) => {
    const response = await gsApi.get(`${players}/${id}`);
    return response;
  }
);

export const getTournamentTeamScorecards = createAsyncThunk(
  "teams/getTournamentTeamScorecards",
  async (id, thunkAPI) => {
    const response = await gsApi.get(
      `${tournamentTeams}/${id}/live-tournament-scorecards`
    );
    return response;
  }
);

export const getTournamentTeamRegistrationOrder = createAsyncThunk(
  "teams/getTournamentTeamRegistrationOrder",
  async (id, thunkAPI) => {
    const response = await gsApi.get(
      `${tournamentTeams}/${id}/registration-order`
    );
    return response;
  }
);

export const getTournamentWaitList = createAsyncThunk(
  "teams/getTournamentWaitList",
  async (id, thunkAPI) => {
    const response = await gsApi.get(`${tournament}/${id}/wait-list-members`);
    return response;
  }
);

export const getTournamentUnassignedPlayers = createAsyncThunk(
  "teams/getTournamentUnassignedPlayers",
  async (id, thunkAPI) => {
    const response = await gsApi.get(`${tournament}/${id}/unassigned-players`);
    return response;
  }
);

export const getTournamentRemovedPlayers = createAsyncThunk(
  "teams/getTournamentRemovedPlayers",
  async (id, thunkAPI) => {
    const response = await gsApi.get(`${tournament}/${id}/removed-players`);
    return response;
  }
);

export const getTournamentTeamRounds = createAsyncThunk(
  "teams/getTournamentTeamRounds",
  async (id, thunkAPI) => {
    const response = await gsApi.get(`${tournamentTeams}/${id}/team-rounds`);
    return response;
  }
);

//create

export const createTournamentTeam = createAsyncThunk(
  "teams/createTournamentTeam",
  async (team, thunkAPI) => {
    let serializedData = getSerializedData(
      team,
      "tournament-teams",
      getOpts(team, [...getTournamentRelationship()])
    );

    const serializedPlayers = team.players.map(
      (p) => getSerializedData({ ...p, id: p.id || p.player?.id || null }, "players", getOpts(p))?.data
    );

    if (!serializedData.data.relationships) {
      serializedData.data["relationships"] = {};
    }
    serializedData.data.relationships["players"] = {
      data: serializedPlayers,
    };

    return await getResponse(
      gsApi.post,
      `${tournamentTeams}/team-rounds-and-players`,
      serializedData,
      thunkAPI
    );
  }
);

export const createUnassignedPlayer = createAsyncThunk(
  "teams/createUnassignedPlayer",
  async (unassignedPlayer, thunkAPI) => {
    let serializedData = getSerializedData(
      unassignedPlayer,
      "players",
      getOpts(unassignedPlayer, [...getTournamentRelationship()])
    );

    return await getResponse(
      gsApi.post,
      `${players}`,
      serializedData,
      thunkAPI
    );
  }
);

//save

export const saveTournamentTeam = createAsyncThunk(
  "teams/saveTournamentTeam",
  async (team, thunkAPI) => {
    let serializedData = getSerializedData(
      team,
      "tournament-teams",
      getOpts(team, [...getTournamentRelationship()])
    );

    const serializedPlayers = team.players.map(
      (p) =>
        getSerializedData(
          { ...p, id: p?.id?.length > 1 ? p.id : null },
          "players",
          getOpts(p)
        )?.data
    );

    if (!serializedData.data.relationships) {
      serializedData.data["relationships"] = {};
    }
    serializedData.data.relationships["players"] = {
      data: serializedPlayers,
    };

    return await getResponse(
      gsApi.patch,
      `${tournamentTeams}/${team.id}`,
      serializedData,
      thunkAPI
    );
  }
);

export const saveTournamentTeamPlayer = createAsyncThunk(
  "teams/saveTournamentTeamPlayer",
  async (player, thunkAPI) => {
    let serializedData = getSerializedData(
      player,
      "players",
      getOpts(player, [...getTournamentTeamRelationship()])
    );
    return await getResponse(
      gsApi.patch,
      `${players}/${player.id}`,
      serializedData,
      thunkAPI
    );
  }
);

export const updateUnassignedPlayer = createAsyncThunk(
  "teams/updateUnassignedPlayer",
  async (player, thunkAPI) => {
    let serializedData = getSerializedData(
      player,
      "players",
      getOpts(player)
    );
    return await getResponse(
      gsApi.patch,
      `${players}/${player.id}`,
      serializedData,
      thunkAPI
    );
  }
);

export const restoreUnassignedPlayer = createAsyncThunk(
  "teams/restoreUnassignedPlayer",
  async (player, thunkAPI) => {
    let serializedData = getSerializedData(
      { ...player, status: "active" },
      "players",
      getOpts(player)
    );
    return await getResponse(
      gsApi.patch,
      `${players}/${player.id}`,
      serializedData,
      thunkAPI
    );
  }
);

export const updateWaitListMember = createAsyncThunk(
  "teams/updateWaitListMember",
  async (member, thunkAPI) => {
    let serializedData = getSerializedData(
      member,
      "wait-list-members",
      getOpts(member)
    );
    return await getResponse(
      gsApi.patch,
      `${waitListMembers}/${member.id}`,
      serializedData,
      thunkAPI
    );
  }
);

export const updateTeamRoundsAndPlayers = createAsyncThunk(
  "teams/updateTeamRoundsAndPlayers",
  async (team, thunkAPI) => {
    let serializedData = getSerializedData(
      team,
      "tournament-teams",
      getOpts(team, [...getTournamentRelationship()])
    );

    const serializedPlayers = team.players.map(
      (p) => getSerializedData({ ...p, id: null }, "players", getOpts(p))?.data
    );

    if (!serializedData.data.relationships) {
      serializedData.data["relationships"] = {};
    }
    serializedData.data.relationships["players"] = {
      data: serializedPlayers,
    };

    return await getResponse(
      gsApi.patch,
      `${tournamentTeams}/team-rounds-and-players`,
      serializedData,
      thunkAPI
    );
  }
);

export const updateTeamRound = createAsyncThunk(
  "teams/updateTeamRound",
  async (teamRound, thunkAPI) => {
    let serializedData = getSerializedData(
      teamRound,
      "team-rounds",
      getOpts(teamRound)
    );

    return await getResponse(
      gsApi.patch,
      `${teamRounds}/${teamRound.id}`,
      serializedData,
      thunkAPI
    );
  }
);

export const saveTeamTeeChoice = createAsyncThunk(
  "teams/saveTeamTeeChoice",
  async (teeChoice, thunkAPI) => {
    let serializedData = getSerializedData(
      teeChoice,
      "round-tee-choices",
      getOpts(teeChoice, [...getIDRelationship("roundTee")])
    );

    return await getResponse(
      gsApi.patch,
      `${roundTeeChoices}/${teeChoice.id}`,
      serializedData,
      thunkAPI
    );
  }
);

export const saveTeamPlayerTeeChoice = createAsyncThunk(
  "teams/saveTeamPlayerTeeChoice",
  async (teeChoice, thunkAPI) => {
    let serializedData = getSerializedData(
      teeChoice,
      "round-tee-player-choices",
      getOpts(teeChoice, [...getIDRelationship("roundTee")])
    );

    return await getResponse(
      gsApi.patch,
      `${roundTeePlayerChoices}/${teeChoice.id}`,
      serializedData,
      thunkAPI
    );
  }
);

//remove team

export const removeTournamentTeam = createAsyncThunk(
  "teams/removeTournamentTeam",
  async (id, thunkAPI) => {
    const response = await getResponse(
      gsApi.delete,
      `${tournamentTeams}/${id}`,
      { data: {} },
      thunkAPI
    );

    return response;
  }
);

//remove player

export const removeTournamentPlayer = createAsyncThunk(
  "teams/removeTournamentPlayer",
  async (id, thunkAPI) => {
    const response = await gsApi.delete(`${players}/${id}`, {
      data: {},
    });
    return response;
  }
);

export const removePlayerFromTeam = createAsyncThunk(
  "teams/removePlayerFromTeam",
  async (context, thunkAPI) => {
    const { player, team, isMainContact, removalOption } = context;

    let response = {};
    if (removalOption === "unassign") {
      // update player to remove tournament team
      const updatedPlayer = {...player, tournamentTeamId: null, tournamentTeam: null, mainContact: false};
      let serializedData = getSerializedData(
        updatedPlayer,
        "players",
        getOpts(updatedPlayer),
      );

      await getResponse(
        gsApi.patch,
        `${players}/${player.id}`,
        serializedData,
        thunkAPI
      );
    } else {
      await gsApi.delete(`${players}/${player.id}`, { data: {} });
    }

    // if the player is the only member of the team, we need to DQ the team
    let updatedTeam = { ...team, players: team.players.filter((p) => p.id !== player.id) };
    if (updatedTeam.players.length === 0) {
      updatedTeam.disqualified = true;
      updatedTeam.disqualificationReason = "disqualified";
      await getResponse(
        gsApi.patch,
        `${tournamentTeams}/${team.id}`,
        getTournamentTeamSerializedData(updatedTeam),
        thunkAPI
      );
      response["team"] = null;
    } else if (isMainContact) {
      // if the player is a main contact, we need to update the team contact
      let newContact = { ...updatedTeam.players[0] } || null;
      if (newContact) {
        // update the player as the new contact
        newContact.mainContact = true;
        let serializedData = getSerializedData(
          newContact,
          "players",
          getOpts(newContact),
        );

        await getResponse(
          gsApi.patch,
          `${players}/${newContact.id}`,
          serializedData,
          thunkAPI
        );
        const playerIndex = updatedTeam.players.findIndex((p) => p.id === newContact.id);
        updatedTeam.players[playerIndex] = newContact;

        updatedTeam.contactName = newContact.name;
        updatedTeam.email = newContact.email;
        updatedTeam.phone = newContact.phone;

        serializedData = getTournamentTeamSerializedData(updatedTeam);

        // update the team with the new contact
        const teamResponse = await getResponse(
          gsApi.patch,
          `${tournamentTeams}/${team.id}`,
          serializedData,
          thunkAPI
        );
        response["team"] = teamResponse;
      }
    } else {
      response["team"] = updatedTeam;
    }

    return response;
  }
);

//validate
export const validateTeamImport = createAsyncThunk(
  "teams/validateTeamImport",
  async (verify, thunkAPI) => {
    const response = await getResponse(
      gsApi.post,
      `${tournament}/${verify?.id}/validate-teams-import`,
      verify,
      thunkAPI
    );
    return response;
  }
);

//import
export const importTeamList = createAsyncThunk(
  "teams/importTeamList",
  async (verify, thunkAPI) => {
    const response = await getResponse(
      gsApi.post,
      `${tournament}/${verify?.id}/import-teams`,
      verify,
      thunkAPI
    );
    return response;
  }
);

export const teamSlice = createSlice({
  name: "teams",
  initialState: {
    tournamentTeams: [],
    tournamentWaitList: [],
    tournamentUnassignedPlayers: [],
    tournamentRemovedPlayers: [],
    currentTeam: {},
    currentTeamPlayer: {},
    currentTeamScorecards: [],
    currentTeamTeeChoices: [],
    currentPlayerTeeChoices: [],
    currentTeamRounds: [],
    currentTeamregistrationOrder: {},
    currentUnassignedPlayer: {},
    validatedTeam: {},
    notifications: [],
    loading: [],
  },
  reducers: {
    setCurrentTeam: (state, action) => {
      state.currentTeam = action.payload;
      const teamIndex = state?.tournamentTeams?.findIndex?.(
        (t) => t.id === action.payload.id
      );
      if (teamIndex >= 0) {
        state.tournamentTeams[teamIndex] = action.payload;
      }
    },
    setCurrentTempPlayer: (state, action) => {
      state.currentTeamPlayer = state.currentTeam?.players?.find?.(
        (p) => p.tempID === action.payload
      );
    },
    setTournamentTeams: (state, action) => {
      state.tournamentTeams = action.payload;
    },
    setValidatedTeam: (state, action) => {
      state.validatedTeam = action.payload;
    },
    notifySlideOut: (state, action) => {
      state.notifications.push(action.payload);
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    setCurrentTeamRounds: (state, action) => {
      state.currentTeamRounds = action.payload;
    },
    setCurrentTeamRound: (state, action) => {
      const index = state.currentTeamRounds?.findIndex?.(
        (ctr) => ctr.id === action.payload.id
      );
      if (index !== -1) {
        state.currentTeamRounds[index] = action.payload;
      }
    },
    setTeamTeeChoice: (state, action) => {
      let index = state.currentTeamTeeChoices?.findIndex?.(
        (c) => c.id === action.payload.id
      );
      state.currentTeamTeeChoices[index] = action.payload;
    },
    setPlayer: (state, action) => {
      let index = state.currentTeam?.players?.findIndex(
        (p) => p.id === action.payload.id
      );
      state.currentTeam.players[index] = action.payload;
    },
    setCurrentUnassignedPlayer: (state, action) => {
      state.currentUnassignedPlayer = action.payload;
    },
    addNotification: (state, action) => {
      state.notifications.unshift(action.payload);
    },
  },
  extraReducers: {
    //get Teams
    ...getThunkResponse(getTournamentTeams, (state, action) => {
      state.tournamentTeams = action.payload.data;
    }),
    //get Team
    ...getThunkResponse(getTournamentTeam, (state, action) => {
      state.currentTeam = action.payload;
    }),
    //get Team Scorecards
    ...getThunkResponse(getTournamentTeamScorecards, (state, action) => {
      state.currentTeamScorecards = action.payload;
    }),
    //get Registration Order
    ...getThunkResponse(getTournamentTeamRegistrationOrder, (state, action) => {
      state.currentTeamregistrationOrder = action.payload;
    }),
    //get wait list
    ...getThunkResponse(getTournamentWaitList, (state, action) => {
      state.tournamentWaitList = action.payload;
    }),
    //get unassigned players
    ...getThunkResponse(getTournamentUnassignedPlayers, (state, action) => {
      state.tournamentUnassignedPlayers = action.payload;
    }),
    //get removed players
    ...getThunkResponse(getTournamentRemovedPlayers, (state, action) => {
      state.tournamentRemovedPlayers = action.payload;
    }),
    //get unassigned player
    ...getThunkResponse(getUnassignedPlayer, (state, action) => {
      state.currentUnassignedPlayer = action.payload;
    }),
    //get player
    ...getThunkResponse(getTournamentTeamPlayer, (state, action) => {
      state.currentTeamPlayer = action.payload;
    }),
    //Create Team
    ...createThunkResponse(
      createTournamentTeam,
      (state, action) => {
        state.tournamentTeams = [action.payload, ...state.tournamentTeams];
        state.currentTeam = action.payload;
      },
      "Tournament Team"
    ),
    //Create Unassigned Player
    ...createThunkResponse(
      createUnassignedPlayer,
      (state, action) => {
        state.tournamentUnassignedPlayers = [...state.tournamentUnassignedPlayers, action.payload];
        state.currentUnassignedPlayer = action.payload;
      },
      "Unassigned Player"
    ),
    //Save Team
    ...saveThunkResponse(
      saveTournamentTeam,
      (state, action) => {
        const teamIndex = state.tournamentTeams.findIndex(
          (t) => t.id === action.payload.id
        );
        state.tournamentTeams[teamIndex] = action.payload;
        if ((state.currentTeam.id === action.payload.id)) {
          state.currentTeam = action.payload;
        }
      },
      "Tournament Team"
    ),
    //Save player
    ...saveThunkResponse(
      saveTournamentTeamPlayer,
      (state, action) => {
        // update the players in the current team
        let players = [...state.currentTeam?.players];
        const mainContact = action.payload.mainContact;
        players.forEach((p, i) => {
          if (p.id === action.payload.id) {
            players[i] = action.payload;
          } else {
            if (mainContact) {
              players[i].mainContact = false;
            }
          }
        });

        // update the team itself in case the main contact changed
        state.currentTeam = {
          ...state.currentTeam,
          ...action.payload.tournamentTeam,
          players: players,
        };
      },
      "Player"
    ),
    //update unassigned player
    ...saveThunkResponse(
      updateUnassignedPlayer,
      (state, action) => {
        const player = action.payload;

        if (player.state === "active") {
          const unassignedIndex = state.tournamentUnassignedPlayers.findIndex?.(
            (p) => p.id === player.id
          );
          state.tournamentUnassignedPlayers[unassignedIndex] = player;
        } else {
          const removedIndex = state.tournamentRemovedPlayers.findIndex?.(
            (p) => p.id === player.id
          );
          state.tournamentRemovedPlayers[removedIndex] = player;
        }
      },
      "Player"
    ),
    ...saveThunkResponse(
      restoreUnassignedPlayer,
      (state, action) => {
        const removedPlayers = state.tournamentRemovedPlayers;
        state.tournamentRemovedPlayers = removedPlayers.filter?.((p) => p.id !== action.payload.id);
        state.tournamentUnassignedPlayers = [...state.tournamentUnassignedPlayers, action.payload];
        state.currentUnassignedPlayer = action.payload;
      },
      "Player"
    ),
    //update wait list member
    ...saveThunkResponse(
      updateWaitListMember,
      (state, action) => {
        const unassignedIndex = state.tournamentWaitList.findIndex?.(
          (p) => p.id === action.payload.id
        );
        state.tournamentWaitList[unassignedIndex] = action.payload;
      },
      "Player"
    ),
    //remove Team
    ...deleteThunkResponse(
      removeTournamentTeam,
      (state, action) => {
        const teams = state.tournamentTeams;
        state.tournamentTeams = teams.filter?.((t) => t.id !== action.meta.arg);
        state.currentTeam = {};
      },
      "Tournament Team"
    ),

    ...deleteThunkResponse(
      removeTournamentPlayer,
      (state, action) => {
        state.currentTeam.players = state?.currentTeam?.players?.filter?.(
          (p) => p.id !== action.meta.arg
        );
        state.currentUnassignedPlayer = { ...state.currentUnassignedPlayer, status: "inactive" };
      },
      "Player"
    ),

    ...deleteThunkResponse(
      removePlayerFromTeam,
      (state, action) => {
        const { team } = action.payload;

        if (!team) {
          // remove team from team list and clear current team
          state.tournamentTeams = state?.tournamentTeams?.filter(
            (t) => t.id !== state.currentTeam.id
          );
          state.currentTeam = {};
        } else {
          const teamIndex = state?.tournamentTeams?.findIndex(
            (t) => t.id === team.id
          );
          state.tournamentTeams[teamIndex] = team;
          state.currentTeam = team;
        }

        // clear current player
        state.currentTeamPlayer = {};
      },
      "Player"
    ),

    //validate team import
    ...validateThunkResponse(
      validateTeamImport,
      (state, action) => {
        let { data } = action.payload;
        const defaultAvatar = "https://api.golfstatus.dev/images/user_round.png";
        const contact = data.player1_name || data.player_name;
        let teamCheck = {
          identifier: data.id,
          contactName: contact,
          players: [],
        };

        //the handicaps come across as they are stored, so the display helpers
        //format them here the way they do everywhere else
        if (contact) {
          teamCheck.players.push({
            name: contact,
            handicap: data.player1_handicap ?? data.player_handicap,
            avatar: defaultAvatar,
          });
        }

        for (let player = 2; player <= 6; player++) {
          if (data[`player${player}_name`]) {
            teamCheck.players.push({
              name: data[`player${player}_name`],
              handicap: data[`player${player}_handicap`],
              avatar: defaultAvatar,
            });
          }
        }

        state.validatedTeam = teamCheck;
      },
      "Team List"
    ),
    //import
    ...uploadThunkResponse(importTeamList, (state, action) => {}, "Team List"),
    ...getThunkResponse(getTournamentTeamRounds, (state, action) => {
      state.currentTeamRounds = action.payload;
    }),
    ...saveThunkResponse(
      updateTeamRound,
      (state, action) => {},
      "Team Round",
      shortDelay
    ),
    ...getThunkResponse(getTournamentTeamTeeChoices, (state, action) => {
      state.currentTeamTeeChoices = action.payload;
    }),
    ...getThunkResponse(getTournamentTeamPlayerTeeChoices, (state, action) => {
      state.currentPlayerTeeChoices = action.payload;
    }),
    ...saveThunkResponse(
      saveTeamTeeChoice,
      (state, action) => {},
      "Tee Choice",
      shortDelay
    ),
    ...saveThunkResponse(
      saveTeamPlayerTeeChoice,
      (state, action) => {},
      "Tee Choice",
      shortDelay
    ),
  },
});

export const {
  setCurrentTeam,
  setTournamentTeams,
  setValidatedTeam,
  notifySlideOut,
  clearNotifications,
  setCurrentTempPlayer,
  setCurrentTeamRound,
  setCurrentTeamRounds,
  setTeamTeeChoice,
  setPlayer,
  setCurrentUnassignedPlayer,
  addNotification,
} = teamSlice.actions;

export const selectCurrentTeam = (state) => state.teams.currentTeam;
export const selectCurrentTeamPlayer = (state) => state.teams.currentTeamPlayer;
export const selectCurrentTeamScorecards = (state) =>
  state.teams.currentTeamScorecards;
export const selectCurrentTeamRegistrationOrder = (state) =>
  state.teams.currentTeamregistrationOrder;
export const selectTournamentTeams = (state) => state.teams.tournamentTeams;
export const selectQualifiedTeams = (state) => state.teams.tournamentTeams.filter((t) => !t.disqualified);
export const selectTournamentWaitList = (state) =>
  state.teams.tournamentWaitList;
export const selectTournamentUnassignedPlayers = (state) =>
  state.teams.tournamentUnassignedPlayers;
export const selectCurrentUnassignedPlayer = (state) => state.teams.currentUnassignedPlayer;
export const selectTournamentRemovedPlayers = (state) => state.teams.tournamentRemovedPlayers;
export const selectTeamsNotifications = (state) => state.teams.notifications;
export const selectLoading = (state) => state.teams.loading;
export const selectValidatedTeam = (state) => state.teams.validatedTeam;
export const selectTournamentTeamRounds = (state) =>
  state.teams.currentTeamRounds;
export const selectTournamentTeamTeeChoices = (state) =>
  state.teams.currentTeamTeeChoices;
export const selectTournamentTeamPlayerTeeChoices = (state) =>
  state.teams.currentPlayerTeeChoices;
export default teamSlice.reducer;

function getTournamentRelationship() {
  const relationships = [];
  relationships.push({
    name: "tournament",
    opt: { ref: "id", included: true },
  });
  return relationships;
}

function getTournamentTeamRelationship() {
  const relationships = [];
  relationships.push({
    name: "tournamentTeam",
    opt: { ref: "id", included: true },
  });
  return relationships;
}

const getTournamentTeamSerializedData = (targetTeam) => {
  let serializedData = getSerializedData(
    targetTeam,
    "tournament-teams",
    getOpts(targetTeam, [...getTournamentRelationship()])
  );

  const serializedPlayers = targetTeam.players.map(
    (p) =>
      getSerializedData(
        { ...p, id: p?.id?.length > 1 ? p.id : null },
        "players",
        getOpts(p)
      )?.data
  );

  if (!serializedData.data.relationships) {
    serializedData.data["relationships"] = {};
  }
  serializedData.data.relationships["players"] = {
    data: serializedPlayers,
  };

  return serializedData;
};
